import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { SideMenuPageContainer } from '../../components/side_menu_page_container/SideMenuPageContainer';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getLanguageUrl, getLanguageFromUrl } from '../../utilities/languageUrls';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { ContentCopy as CopyIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  selectState,
  convertJsonToCSharpAsync,
  inputJsonChanged,
  classNameChanged,
  propertiesToggled,
  nullableReferenceTypesToggled,
  pascalCaseToggled,
  clearGenerated,
  clearAll,
} from './JsonToCSharpSlice';
import { HomeCard } from '../../components/home_card/HomeCard';
import Base64EncoderImage from '../../assets/Base64Encoder.webp';
import CsvJsonConverterImage from '../../assets/CsvJsonConverter.webp';
import CaseConverterImage from '../../assets/CaseConverter.webp';
import AdSense from '../../components/adsense/AdSense';
import { ADSENSE_SLOTS } from '../../config/adsense';

export default function JsonToCSharp() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();
  const location = useLocation();
  const currentLanguage = getLanguageFromUrl(location.pathname);

  const handleConvert = () => {
    dispatch(convertJsonToCSharpAsync());
  };

  const handleClearGenerated = () => {
    dispatch(clearGenerated());
  };

  const handleClearAll = () => {
    dispatch(clearAll());
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleCopyAll = () => {
    if (state.generatedCSharp) {
      handleCopyToClipboard(state.generatedCSharp);
    }
  };

  const handleLoadSample = () => {
    const sampleJson = `{
  "calculator": {
    "name": "NumberChomper Calculator",
    "version": "2.1.0",
    "features": ["basic", "scientific", "converter", "generator"],
    "isActive": true,
    "lastUpdated": "2024-01-15"
  },
  "mathematicalOperations": {
    "basic": ["addition", "subtraction", "multiplication", "division"],
    "advanced": ["exponentiation", "logarithm", "trigonometry", "statistics"],
    "precision": 15,
    "supportsComplexNumbers": false
  },
  "conversionTools": {
    "length": ["meters", "feet", "inches", "kilometers", "miles"],
    "weight": ["kilograms", "pounds", "ounces", "grams"],
    "temperature": ["celsius", "fahrenheit", "kelvin"],
    "currency": ["USD", "EUR", "GBP", "JPY", "CAD"]
  },
  "generatorSettings": {
    "uuidFormat": "standard",
    "passwordLength": 16,
    "caseConverterOptions": ["lowercase", "uppercase", "camelcase", "snakecase"],
    "base64Encoding": "standard"
  },
  "userPreferences": {
    "defaultLanguage": "en",
    "theme": "dark",
    "decimalPlaces": 4,
    "scientificNotation": true,
    "autoSave": true
  }
}`;
    dispatch(inputJsonChanged(sampleJson));
  };

  // Related tools data
  const relatedTools = [
    {
      name: 'base64Encoder',
      link: getLanguageUrl(currentLanguage, 'base64Encoder'),
      image: Base64EncoderImage,
    },
    {
      name: 'csvJsonConverter',
      link: getLanguageUrl(currentLanguage, 'csvJsonConverter'),
      image: CsvJsonConverterImage,
    },
    {
      name: 'caseConverter',
      link: getLanguageUrl(currentLanguage, 'caseConverter'),
      image: CaseConverterImage,
    },
  ];

  return (
    <SideMenuPageContainer
      pageTitle={t('jsonToCSharp.title')}
      pageSeoTitle={t('jsonToCSharp.seoTitle')}
      pageDescription={t('jsonToCSharp.description')}
    >
      <Helmet>
        <title>{t('jsonToCSharp.seoTitle')}</title>
        <meta name="description" content={t('jsonToCSharp.description')} />
      </Helmet>

      <Box sx={{ p: 3 }}>
        {/* Main Heading */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ color: '#FFE4B5', mb: 2 }}>
            {t('jsonToCSharp.mainHeading')}
          </Typography>
          <Typography variant="h6" sx={{ color: '#b8e6f7', opacity: 0.8 }}>
            {t('jsonToCSharp.subHeading')}
          </Typography>
        </Box>

        {/* Sample JSON Button */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleLoadSample}
            startIcon={<RefreshIcon />}
            sx={{
              color: '#b8e6f7',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: '#b8e6f7',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {t('jsonToCSharp.loadSample')}
          </Button>
        </Box>

        <Grid2 container spacing={3}>
          {/* Input Section */}
          <Grid2 xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: '#404f65',
                borderRadius: '10px',
                height: 'fit-content',
              }}
            >
              <Typography variant="h6" sx={{ color: '#c1edff', mb: 2 }}>
                {t('jsonToCSharp.inputJsonLabel')}
              </Typography>
              
                                            <TextField
                 fullWidth
                 multiline
                 rows={12}
                 value={state.inputJson}
                 onChange={(e) => dispatch(inputJsonChanged(e.target.value))}
                 placeholder={t('jsonToCSharp.inputPlaceholder')}
                 variant="outlined"
                 InputProps={{
                   style: {
                     color: '#c1edff',
                     fontFamily: 'monospace',
                   },
                 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#b8e6f7',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.7rem !important',
                  },
                }}
               />

              {/* Options Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ color: '#c1edff', mb: 2 }}>
                  {t('jsonToCSharp.optionsTitle')}
                </Typography>
                
                <Grid2 container spacing={2}>
                  <Grid2 xs={12}>
                    <TextField
                      fullWidth
                      label={t('jsonToCSharp.classNameLabel')}
                      value={state.className}
                      onChange={(e) => dispatch(classNameChanged(e.target.value))}
                      size="small"
                      InputProps={{
                        style: { color: '#c1edff' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#b8e6f7',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  </Grid2>
                  
                  <Grid2 xs={12}>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={state.useProperties}
                            onChange={(e) => dispatch(propertiesToggled(e.target.checked))}
                            sx={{
                              color: '#b8e6f7',
                              '&.Mui-checked': {
                                color: '#b8e6f7',
                              },
                            }}
                          />
                        }
                        label={t('jsonToCSharp.useProperties')}
                        sx={{ color: '#c1edff' }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={state.useNullableReferenceTypes}
                            onChange={(e) => dispatch(nullableReferenceTypesToggled(e.target.checked))}
                            sx={{
                              color: '#b8e6f7',
                              '&.Mui-checked': {
                                color: '#b8e6f7',
                              },
                            }}
                          />
                        }
                        label={t('jsonToCSharp.useNullableReferenceTypes')}
                        sx={{ color: '#c1edff' }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={state.usePascalCase}
                            onChange={(e) => dispatch(pascalCaseToggled(e.target.checked))}
                            sx={{
                              color: '#b8e6f7',
                              '&.Mui-checked': {
                                color: '#b8e6f7',
                              },
                            }}
                          />
                        }
                        label={t('jsonToCSharp.usePascalCase')}
                        sx={{ color: '#c1edff' }}
                      />
                    </Stack>
                  </Grid2>
                </Grid2>
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleConvert}
                  disabled={state.isConverting || !state.inputJson.trim()}
                  sx={{
                    backgroundColor: '#b8e6f7',
                    color: '#1a1a1a',
                    '&:hover': {
                      backgroundColor: '#a0d4e7',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  {state.isConverting ? t('jsonToCSharp.converting') : t('jsonToCSharp.convertButton')}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleClearGenerated}
                  disabled={!state.generatedCSharp}
                  sx={{
                    color: '#b8e6f7',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: '#b8e6f7',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:disabled': {
                      color: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {t('jsonToCSharp.clearButton')}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  sx={{
                    color: '#b8e6f7',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: '#b8e6f7',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {t('jsonToCSharp.clearAll')}
                </Button>
              </Stack>
            </Paper>
          </Grid2>

          {/* Output Section */}
          <Grid2 xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: '#404f65',
                borderRadius: '10px',
                height: 'fit-content',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#c1edff' }}>
                  {t('jsonToCSharp.generatedCSharpLabel')}
                </Typography>
                
                {state.generatedCSharp && (
                  <Stack direction="row" spacing={1}>
                    <Tooltip title={t('jsonToCSharp.copyTooltip')}>
                      <IconButton
                        onClick={handleCopyAll}
                        size="small"
                        sx={{ color: '#98c8db' }}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Box>

              {state.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {state.error}
                </Alert>
              )}

              <TextField
                fullWidth
                multiline
                rows={12}
                value={state.generatedCSharp}
                InputProps={{
                  readOnly: true,
                  style: {
                    color: '#c1edff',
                    fontFamily: 'monospace',
                  },
                }}
                placeholder={t('jsonToCSharp.outputPlaceholder')}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#b8e6f7',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.7rem !important',
                  },
                }}
              />
            </Paper>
          </Grid2>
        </Grid2>

        {/* Content Section */}
        <Box sx={{ mt: 6 }}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h4" sx={{ color: '#FFE4B5', mb: 3, textAlign: 'left' }}>
            {t('jsonToCSharp.content.freeHeading')}
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#b8e6f7', mb: 4, textAlign: 'left', maxWidth: 800, mx: 'auto' }}>
            {t('jsonToCSharp.content.freeDescription')}
          </Typography>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#FFE4B5', mb: 2 }}>
                {t('jsonToCSharp.content.whatIsHeading')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#b8e6f7', opacity: 0.9 }}>
                {t('jsonToCSharp.content.whatIsDescription')}
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#FFE4B5', mb: 2 }}>
                {t('jsonToCSharp.content.featuresHeading')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#b8e6f7', opacity: 0.9 }}>
                {t('jsonToCSharp.content.featuresDescription')}
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#FFE4B5', mb: 2 }}>
                {t('jsonToCSharp.content.useCasesHeading')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#b8e6f7', opacity: 0.9 }}>
                {t('jsonToCSharp.content.useCasesDescription')}
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#FFE4B5', mb: 2 }}>
                {t('jsonToCSharp.content.howToHeading')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#b8e6f7', opacity: 0.9 }}>
                {t('jsonToCSharp.content.howToDescription')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Related Tools */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ color: '#FFE4B5', mb: 3, textAlign: 'center' }}>
            {t('jsonToCSharp.relatedTools')}
          </Typography>
          
          <Grid2 container spacing={3} columns={{ xs: 6, sm: 6, md: 12 }}>
            {relatedTools.map((tool) => (
              <Grid2 xs={6} key={tool.name}>
                <HomeCard
                  languageResource={tool.name}
                  link={tool.link}
                  image={tool.image}
                  smallCard={true}
                />
              </Grid2>
            ))}
          </Grid2>
        </Box>
      </Box>
    </SideMenuPageContainer>
  );
}
